import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Mail, Phone, MapPin, Send, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Projets mis en avant sur la page Contact (6 sélectionnés)
const FEATURED = [
  {
    id: "smartbtp",
    title: "Smart BTP — Devis Hiérarchique",
    desc: "Module Odoo 19 production : numérotation hiérarchique automatique (1, 1.1, 1.1.1), sous-totaux par section, pliage/dépliage et PDF conforme au format BTP.",
    badge: "Odoo 19 · Réel",
    badgeCls: "bg-blue-100 text-blue-700",
    img: "/projects/btpv2_lines.png",
    tags: ["Numérotation auto", "Sous-totaux", "PDF BTP"],
    tagCls: "bg-blue-50 text-blue-700 border-blue-100",
  },
  {
    id: "aowatch",
    title: "AO Watch — Veille Marchés Publics",
    desc: "Plateforme de veille automatisée des appels d'offres : scoring IA de pertinence, alertes personnalisées Réunion / Mayotte / Métropole et suivi des candidatures.",
    badge: "Maquette · IA",
    badgeCls: "bg-amber-100 text-amber-700",
    img: "/projects/aowatch.png",
    tags: ["Scoring IA", "Alertes", "DOM"],
    tagCls: "bg-amber-50 text-amber-700 border-amber-100",
  },
  {
    id: "gymtech",
    title: "GymTech — Gestion SAV Salles de Sport",
    desc: "Application de suivi des interventions techniques multi-sites pour équipements sportifs : planning, urgences, coûts par salle et rapport mensuel automatique.",
    badge: "Maquette · Multi-sites",
    badgeCls: "bg-green-100 text-green-700",
    img: "/projects/gymtech.png",
    tags: ["SAV", "Planning", "Multi-sites"],
    tagCls: "bg-green-50 text-green-700 border-green-100",
  },
  {
    id: "idmat",
    title: "IDMAT OI — Suivi ADV Configurateur",
    desc: "Module Odoo 19 pour IDMAT OI : pipeline ADV 9 étapes, configurateur d'engins (LiuGong, Rippa), checklist documents et suivi de progression dossier.",
    badge: "Maquette Odoo",
    badgeCls: "bg-violet-100 text-violet-700",
    img: "/projects/idmat_configurateur.png",
    tags: ["Configurateur", "ADV", "Pipeline"],
    tagCls: "bg-violet-50 text-violet-700 border-violet-100",
  },
  {
    id: "emergent",
    title: "Emergent — Tâches & Messagerie",
    desc: "App mobile-first iOS-style de gestion des tâches et messagerie collaborative interne : listes partagées, priorités, statistiques hebdo et messages d'équipe.",
    badge: "Maquette · Mobile",
    badgeCls: "bg-cyan-100 text-cyan-700",
    img: "/projects/emergent.png",
    tags: ["iOS-style", "Tâches", "Messagerie"],
    tagCls: "bg-cyan-50 text-cyan-700 border-cyan-100",
  },
  {
    id: "smartbuilder",
    title: "SmartBuilder — Studio Apps par IA",
    desc: "Studio no-code de génération d'applications métier par IA : décris l'app en langage naturel, l'IA génère le code et déploie. Projets SmartRent, menu snack, catalogue IDMAT.",
    badge: "Maquette · IA générative",
    badgeCls: "bg-indigo-100 text-indigo-700",
    img: "/projects/smartbuilder.png",
    tags: ["IA générative", "No-code", "SaaS"],
    tagCls: "bg-indigo-50 text-indigo-700 border-indigo-100",
  },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", company: "", message: "" });
        toast.success("Message envoyé avec succès !");
      } else {
        toast.error("Erreur lors de l'envoi du message");
      }
    } catch {
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { icon: Mail,   label: "Email",     value: "contact@novaforge.pro",                    href: "mailto:contact@novaforge.pro" },
    { icon: Phone,  label: "Téléphone", value: "+262 692 69 42 94",                      href: "tel:+262692694294" },
    { icon: MapPin, label: "Adresse",   value: "27 Avenue des Artisans\n97436 Saint-Leu", href: null },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white" />
        <div className="nova-container relative z-10 max-w-3xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6" data-testid="contact-title">
            Parlons de votre <span className="nova-gradient-text">projet Odoo</span>
          </h1>
          <p className="text-lg text-slate-600">
            Une question, un projet Odoo en tête ? Notre équipe répond sous 24h ouvrées.
          </p>
        </div>
      </section>

      {/* ── Réalisations ── */}
      <section className="py-20 bg-slate-50">
        <div className="nova-container">
          <div className="text-center mb-14">
            <span className="inline-block text-indigo-600 text-xs font-bold tracking-widest uppercase mb-3">
              Portfolio
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Quelques <span className="nova-gradient-text">réalisations</span>
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto text-sm">
              Modules Odoo en production, applications métier et plateformes IA déployés à La Réunion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {FEATURED.map((p) => (
              <div
                key={p.id}
                className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-lg hover:border-indigo-100 transition-all duration-300"
              >
                {/* Image */}
                <div className="relative overflow-hidden" style={{ height: 200 }}>
                  <img
                    src={p.img}
                    alt={p.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <Badge className={`${p.badgeCls} border-0 text-xs font-semibold`}>{p.badge}</Badge>
                  </div>
                </div>
                {/* Corps */}
                <div className="p-5">
                  <h3 className="font-bold text-slate-900 mb-2 text-base leading-snug">{p.title}</h3>
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed line-clamp-3">{p.desc}</p>
                  <div className="flex flex-wrap gap-1">
                    {p.tags.map((t) => (
                      <span key={t} className={`text-xs px-2 py-0.5 rounded-full border ${p.tagCls}`}>{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/portfolio">
              <Button variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50">
                Voir toutes les réalisations
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Formulaire Contact ── */}
      <section className="nova-section bg-white">
        <div className="nova-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Contactez-nous</h2>
            <p className="text-slate-500 text-sm">Un projet en tête ? Décrivez-le, on revient vers vous sous 24h.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Formulaire */}
            <div>
              {submitted ? (
                <div className="bg-green-50 rounded-2xl p-12 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Message envoyé !</h2>
                  <p className="text-slate-600 mb-6">Merci de nous avoir contactés. Nous vous répondrons sous 24h.</p>
                  <Button variant="outline" onClick={() => setSubmitted(false)}>
                    Envoyer un autre message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nom complet *</Label>
                        <Input id="name" name="name" value={formData.name} onChange={handleChange}
                          placeholder="Prénom Nom" required className="nova-input" data-testid="contact-name-input" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange}
                          placeholder="vous@exemple.com" required className="nova-input" data-testid="contact-email-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Entreprise</Label>
                      <Input id="company" name="company" value={formData.company} onChange={handleChange}
                        placeholder="Nom de votre entreprise" className="nova-input" data-testid="contact-company-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea id="message" name="message" value={formData.message} onChange={handleChange}
                        placeholder="Décrivez votre projet Odoo ou posez-nous vos questions…"
                        required rows={6} className="nova-input resize-none" data-testid="contact-message-input" />
                    </div>
                    <Button type="submit" className="nova-btn-primary w-full sm:w-auto"
                      disabled={loading} data-testid="contact-submit-btn">
                      {loading
                        ? <><span className="animate-spin mr-2">⏳</span>Envoi…</>
                        : <><Send className="w-4 h-4 mr-2" />Envoyer le message</>}
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* Coordonnées */}
            <div className="lg:pl-8">
              <div className="space-y-6 mb-10">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-start gap-4" data-testid={`contact-info-${i}`}>
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                      {item.href
                        ? <a href={item.href} className="text-slate-900 font-medium hover:text-indigo-600 transition-colors">{item.value}</a>
                        : <p className="text-slate-900 font-medium whitespace-pre-line">{item.value}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl overflow-hidden border border-slate-200 mb-8">
                <img src="https://images.unsplash.com/photo-1617952236317-0bd127407984?w=800&q=80"
                  alt="Saint-Leu, La Réunion" className="w-full h-64 object-cover" />
              </div>

              <div className="p-6 bg-indigo-50 rounded-2xl border border-indigo-100">
                <h3 className="font-semibold text-slate-900 mb-2">Un projet Odoo précis ?</h3>
                <p className="text-slate-600 text-sm">
                  Utilisez notre{" "}
                  <a href="/onboarding" className="text-indigo-600 hover:underline font-medium">formulaire de cadrage</a>
                  {" "}pour nous donner tous les détails — on revient avec une première analyse sous 24h.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
